import { archivedTeamsQuery, teamsQuery } from "@common/api/teams"
import type { TeamDetailPublic } from "@common/client"
import { Button } from "@common/components/ui/button"
import { GridTable } from "@common/components/ui/grid-table"
import { useHasPermission } from "@common/hooks/use-permissions"
import { formatCount } from "@common/lib/number"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { UsersRound } from "lucide-react"
import { z } from "zod"
import { FilterBar } from "@/components/layout/filter-bar"
import { ListEmpty } from "@/components/layout/list-empty"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import {
  SettingsTableCell,
  SettingsTableHead,
  SettingsTableHeader,
  SettingsTableRow,
} from "@/routes/_layout/settings/_panel/-components/settings-table"

const COLUMNS = "grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,0.5fr)]"

const filterSchema = z.object({
  archived: z.boolean().optional().catch(false),
  search: z.string().optional().catch(""),
})

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/teams/_list",
)({
  validateSearch: (search) => filterSchema.parse(search),
  loaderDeps: ({ search }) => ({ archived: search.archived }),
  loader: ({ context: { queryClient }, deps }) =>
    queryClient.ensureQueryData(
      deps.archived ? archivedTeamsQuery() : teamsQuery(),
    ),
  component: TeamsPage,
})

function TeamsPage(): React.ReactElement {
  const { search = "" } = Route.useSearch()

  return (
    <div className="flex h-full flex-col">
      <FilterBar>
        <Toolbar search={search} />
      </FilterBar>
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto [--filter-bar-height:0px]">
          <TeamResults search={search} />
        </div>
        <Outlet />
      </div>
    </div>
  )
}

function Toolbar({ search }: { search: string }): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })
  const { archived = false } = Route.useSearch()
  const canCreate = useHasPermission("team:create")
  const filter = (next: string) =>
    navigate({
      search: (previous) => ({ ...previous, search: next || undefined }),
      replace: true,
    })

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={["active", "archived"] as const}
          tab={archived ? "archived" : "active"}
          label={(tab) => (tab === "active" ? "Active" : "Archived")}
          name="Record status"
          onSelect={(tab) =>
            navigate({
              search: (previous) => ({
                ...previous,
                archived: tab === "archived" || undefined,
              }),
              replace: true,
            })
          }
        />
      }
      actions={
        <>
          <NameFilter search={search} onSearch={filter} />
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/settings/workspace/teams/create">New team</Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function TeamResults({ search }: { search: string }): React.ReactElement {
  const { archived = false } = Route.useSearch()
  const { data } = useSuspenseQuery(
    archived ? archivedTeamsQuery() : teamsQuery(),
  )
  const params = useParams({ strict: false })
  const teamId = "teamId" in params ? params.teamId : undefined
  const needle = search.toLowerCase()
  const teams = data.data.filter((team) =>
    team.name.toLowerCase().includes(needle),
  )

  if (teams.length === 0) {
    return <EmptyState search={search} archived={archived} />
  }

  return (
    <GridTable className={COLUMNS}>
      <SettingsTableHeader>
        <SettingsTableHead className="pl-6">Name</SettingsTableHead>
        <SettingsTableHead>Department</SettingsTableHead>
        <SettingsTableHead className="pr-6">Members</SettingsTableHead>
      </SettingsTableHeader>
      {teams.map((team) => (
        <TeamRow key={team.id} team={team} selected={team.id === teamId} />
      ))}
    </GridTable>
  )
}

function TeamRow({
  team,
  selected,
}: {
  team: TeamDetailPublic
  selected: boolean
}): React.ReactElement {
  return (
    <SettingsTableRow
      asChild
      archived={Boolean(team.deleted_at)}
      aria-selected={selected}
      selected={selected}
    >
      <Link
        to="/settings/workspace/teams/$teamId"
        params={{ teamId: team.id }}
        search={true}
      >
        <SettingsTableCell className="pl-6">
          <span className="truncate">{team.name}</span>
        </SettingsTableCell>
        <SettingsTableCell className="text-muted-foreground">
          <span className="truncate">{team.department.name}</span>
        </SettingsTableCell>
        <SettingsTableCell className="pr-6 text-muted-foreground">
          {formatCount(team.members.length)}
        </SettingsTableCell>
      </Link>
    </SettingsTableRow>
  )
}

function EmptyState({
  search,
  archived,
}: {
  search: string
  archived: boolean
}): React.ReactElement {
  const title = search
    ? "No matching teams"
    : archived
      ? "No archived teams"
      : "No teams"
  const body = search
    ? "Try a different name."
    : archived
      ? "Archived teams appear here."
      : "Group people into teams within a department."

  return <ListEmpty icon={UsersRound} title={title} body={body} />
}
